/**
 * Shared numeric status codes for entity lifecycle management.
 * Used for questionnaires, questions, rules and other resources to represent their state.
 *
 * - Pending (0): Initial state, awaiting action
 * - Active (1): Currently active and in use
 * - Suspended (2): Temporarily disabled or paused
 * - Completed (3): Finished or completed state
 * - Deleted (4): Soft-deleted (hidden but not permanently removed)
 */

import type { ChipColor } from "./statusColors";

export const GenericStatus = {
  Pending: 0,
  Active: 1,
  Suspended: 2,
  Completed: 3,
  Deleted: 4,
} as const;

export type GenericStatus = (typeof GenericStatus)[keyof typeof GenericStatus];

/**
 * Human‑readable labels for generic lifecycle statuses.
 */
export const GenericStatusLabels: Record<GenericStatus, string> = {
  [GenericStatus.Pending]: "Pending",
  [GenericStatus.Active]: "Active",
  [GenericStatus.Suspended]: "Suspended",
  [GenericStatus.Completed]: "Completed",
  [GenericStatus.Deleted]: "Deleted",
} as const;

/**
 * Short descriptions for tooltips and dialogs.
 */
export const GenericStatusDescriptions: Record<GenericStatus, string> = {
  [GenericStatus.Pending]: "Awaiting review or activation",
  [GenericStatus.Active]: "Currently active and in use",
  [GenericStatus.Suspended]: "Temporarily disabled or paused",
  [GenericStatus.Completed]: "Completed and no longer changing",
  [GenericStatus.Deleted]: "Soft deleted and hidden from normal views",
} as const;

/**
 * MUI Chip colors for generic lifecycle statuses.
 */
export const GenericStatusColors: Record<GenericStatus, ChipColor> = {
  [GenericStatus.Pending]: "warning",
  [GenericStatus.Active]: "success",
  [GenericStatus.Suspended]: "error",
  [GenericStatus.Completed]: "info",
  [GenericStatus.Deleted]: "default",
} as const;
